<template>
  <div
    id="pulseCalendar"
    class="pulseCalendar"
  >
    <FullCalendar
      ref="pulseCalendar"
      default-view="dayGridMonth"
      :header="header"
      :fixed-week-count="false"
      :plugins="calendarPlugins"
      :events="calendarDate"
      :content-height="252"
      :first-day="1"
      :display-event-time="false"
      :column-header-text="columnHeaderText"
      :title-format="{year: 'numeric', month: 'long'} "
    />
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

export default {
  components: {
    FullCalendar,
  },

  props: {
    pulseDate: {
      type: Object,
      default: null,
    },

    pulseNumber: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      header: {
        left: 'prev,next',
        center: 'title',
        right: 'today',
      },
      calendarPlugins: [
        dayGridPlugin,
        interactionPlugin,
      ],
      event: [],
    };
  },

  computed: {
    calendarDate() {
      const event = [];

      // if (this.pulseDate && this.pulseDate.dateList.length > 0) {
      if (this.pulseDate) {
        // Event Period range
        if (this.pulseDate.start_date) { // No range displayed if no start date
          let rangeEnd;
          if (this.pulseDate.end_date) {
            rangeEnd = this.$moment(this.pulseDate.end_date)
              .add(1, 'days')
              .format('YYYY-MM-DD');
          } else {
            rangeEnd = this.$moment()
              .add(1, 'days')
              .format('YYYY-MM-DD');
          }

          event.push(
            {
              start: this.pulseDate.start_date,
              end: rangeEnd,
              backgroundColor: this.pulseColor,
              rendering: 'background',
            },
          );
        }

        // Period Start
        if (this.pulseDate.start_date) {
          event.push(
            {
              start: this.pulseDate.start_date,
              end: this.pulseDate.start_date,
              backgroundColor: 'rgba(0, 0, 0, 0)',
              borderColor: 'rgba(255, 255, 255, 0.7)',
              overlap: false,
            },
          );
        }

        // Period End
        if (this.pulseDate.end_date) {
          event.push(
            {
              start: this.pulseDate.end_date,
              end: this.pulseDate.end_date,
              backgroundColor: 'rgba(0, 0, 0, 0)',
              borderColor: 'rgba(255, 255, 255, 0.7)',
              overlap: false,
            },
          );
        }

        this.pulseDate.dateList.forEach((date) => (
          event.push({
            start: date,
            end: date,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            borderColor: 'rgba(255, 255, 255, 0.7)',
            overlap: false,
          })
        ));
      }
      return event;
    },

    pulseColor() {
      switch (this.pulseNumber) {
        case 1:
          return '#f3c065';
        case 2:
          return '#3aaace';
        case 3:
          return '#b79eff';
        default:
          return null;
      }
    },
  },

  methods: {
    columnHeaderText(date) {
      let value;
      switch (date.getDay()) {
        case 0:
          value = 'Sun';
          break;
        case 1:
          value = 'Mon';
          break;
        case 2:
          value = 'Tue';
          break;
        case 3:
          value = 'Wed';
          break;
        case 4:
          value = 'Thu';
          break;
        case 5:
          value = 'Fri';
          break;
        case 6:
          value = 'Sat';
          break;
        default:
          value = '-';
      }

      return value;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~@fullcalendar/core/main.css';
  @import '~@fullcalendar/daygrid/main.css';

  #pulseCalendar {
    padding: 15px 5px 0;
  }
</style>

<style lang='scss'>
  #pulseCalendar {
    .fc {
      th,
      td {
        border-width: 0;
      }

      .fc-header-toolbar {
        margin-bottom: 15px;

        .fc-center h2 {
          font-size: 16px;
        }

        .fc-button {
          background-color: transparent;
          border-color: transparent;
          box-shadow: none;
          color: var(--themeFontColor);

          &.fc-prev-button,
          &.fc-next-button {
            padding: 2px 10px;
          }

          &.fc-next-button {
            border-top-right-radius: 25px;
            border-bottom-right-radius: 25px;
          }

          &.fc-prev-button {
            border-top-left-radius: 25px;
            border-bottom-left-radius: 25px;
          }

          &.fc-today-button {
            border-color: var(--themeFontColor);
            padding: 2px 15px;
            border-radius: 25px;
            text-transform: capitalize;
            font-size: 14px;
          }
        }
      }

      .fc-view-container {
        .fc-widget-header {
          padding-bottom: 5px;
          color: rgba(255, 255, 255, 0.5);
        }

        .fc-head {
          .fc-day-header {
            font-weight: normal;
            font-size: 12px;
          }
        }

        .fc-body {
          .fc-row {
            border-bottom: 1px dashed var(--themeFontColor);
            padding-bottom: 5px;

            .fc-bgevent-skeleton,
            .fc-row .fc-highlight-skeleton {
              bottom: 5px;
            }

            &:last-child {
              border-bottom: none;
            }
          }
        }

        .fc-widget-content {
          .fc-day-grid-container {
            height: 270px !important;
          }
        }

        .fc-event-container {
          position: relative;
          width: 100%;
          top: -27px;
          z-index: -1;

          .fc-event {
            cursor: pointer;
            border-radius: 30px;
            width: 100%;
            height: 30px;
            margin: -1px auto 0;
            padding: 3px;
            box-sizing: border-box;
          }
        }

        .fc-row.fc-week {
          height: 30px !important;
          min-height: 30px;
          margin: 5px 0;
        }
      }

      .fc-bgevent {
        border-radius: 30px;
        padding: 4px 0;
        box-sizing: border-box;
        opacity: 1;
      }

      &.fc-ltr .fc-dayGrid-view .fc-day-top .fc-day-number {
        float: unset;
      }

      .fc-day-top {
        text-align: center;
        padding: 5px 0;
        font-size: 14px;
      }
    }

    .fc-today {
      background: transparent !important;
      border: none !important;
    }
  }

</style>
